import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import { Card } from '@/components/ui/card';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PeriodInput } from '@/components/ui/period-input';
import { Textarea } from '@/components/ui/textarea';
import { useEntityForm } from '@/hooks/useEntityForm';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

import type { TariffFormValues } from './tariff-input';

const tariffFormSchema: z.ZodType<TariffFormValues> = z.object({
	name: z.string().min(1, 'Name is required').max(255),
	version: z.string().max(50).default(''),
	description: z.string().max(1000).default(''),
	entryGracePeriodMinutes: z
		.number({ required_error: 'Entry grace period is required' })
		.min(0, 'Must be 0 or greater'),
	postPaymentGraceMinutes: z
		.number({ required_error: 'Post-payment grace is required' })
		.min(0, 'Must be 0 or greater'),
	freeExitMinutes: z
		.number({ required_error: 'Free exit minutes is required' })
		.min(0, 'Must be 0 or greater'),
	lostTicketFixedCharge: z.string().default(''),
	lostTicketRateId: z.string().default(''),
	reEntryChargeRateId: z.string().default(''),
	sourceTariffId: z.string().default(''),
	status: z.number().default(1)
}) as any;

interface TariffFormProps {
	mode: 'create' | 'edit' | 'clone';
	initialData?: any;
	onSubmit: (values: TariffFormValues) => Promise<void> | void;
	isSubmitting: boolean;
	headerTitle: string;
	onBack?: () => void;
}

const buildInitialValues = (data?: any): TariffFormValues | undefined => {
	if (!data) return undefined;

	return {
		name: data.name || '',
		version: data.version || '',
		description: data.description || '',
		entryGracePeriodMinutes: data.entryGracePeriodMinutes ?? undefined,
		postPaymentGraceMinutes: data.postPaymentGraceMinutes ?? undefined,
		freeExitMinutes: data.freeExitMinutes ?? undefined,
		lostTicketFixedCharge:
			data.lostTicketFixedCharge != null
				? String(data.lostTicketFixedCharge)
				: '',
		lostTicketRateId: data.lostTicketRateId || '',
		reEntryChargeRateId: data.reEntryChargeRateId || '',
		sourceTariffId: data.sourceTariffId || '',
		status: data.status ?? 1
	};
};

export const TariffForm = ({
	mode,
	initialData,
	onSubmit,
	isSubmitting,
	headerTitle,
	onBack
}: TariffFormProps) => {
	const { t } = useTranslation('tariffs');
	const nameInputRef = useRef<HTMLInputElement>(null);

	const memoizedInitialData = useMemo(
		() => buildInitialValues(initialData),
		[initialData]
	);

	const form = useEntityForm<TariffFormValues>({
		mode,
		schema: tariffFormSchema,
		defaultValues: {
			name: '',
			version: '',
			description: '',
			entryGracePeriodMinutes: undefined,
			postPaymentGraceMinutes: undefined,
			freeExitMinutes: undefined,
			lostTicketFixedCharge: '',
			lostTicketRateId: '',
			reEntryChargeRateId: '',
			sourceTariffId: '',
			status: 1
		},
		initialData: memoizedInitialData
	});

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	useEffect(() => {
		setTimeout(() => {
			nameInputRef.current?.focus();
		}, 0);
	}, [mode]);

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				rightContent={
					<FormActionButtons
						mode={mode}
						isSubmitting={isSubmitting}
						validationErrors={form.formState.errors}
						formId="tariff-form"
					/>
				}
			/>
			<Form {...form}>
				<form
					id="tariff-form"
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-6"
				>
					{/* General Information */}
					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t('form.generalInfo')}
							</h2>
						</div>

						<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.name')} *
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												ref={nameInputRef}
												autoComplete="off"
												maxLength={255}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="version"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.version')}
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												autoComplete="off"
												maxLength={50}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
</div>

						<FormField
							control={form.control}
							name="description"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.description')}
									</FormLabel>
									<FormControl>
										<Textarea
											{...field}
											autoComplete="off"
											maxLength={1000}
											rows={3}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</Card>

					{/* Grace Periods & Timing */}
					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t('form.gracePeriods')}
							</h2>
						</div>

						<div className="grid gap-6 grid-cols-1 md:grid-cols-3">
							<FormField
								control={form.control}
								name="entryGracePeriodMinutes"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.entryGracePeriod')} *
										</FormLabel>
										<FormControl>
											<PeriodInput
												value={field.value}
												onChange={field.onChange}
												aria-invalid={!!form.formState.errors.entryGracePeriodMinutes}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="postPaymentGraceMinutes"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.postPaymentGrace')} *
										</FormLabel>
										<FormControl>
											<PeriodInput
												value={field.value}
												onChange={field.onChange}
												aria-invalid={!!form.formState.errors.postPaymentGraceMinutes}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="freeExitMinutes"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.freeExitMinutes')} *
										</FormLabel>
										<FormControl>
											<PeriodInput
												value={field.value}
												onChange={field.onChange}
												aria-invalid={!!form.formState.errors.freeExitMinutes}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</Card>

					{/* Lost Ticket & Re-entry */}
					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t('form.lostTicketAndReentry')}
							</h2>
						</div>

						<div className="grid gap-6 grid-cols-1 md:grid-cols-3">
							<FormField
								control={form.control}
								name="lostTicketFixedCharge"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.lostTicketFixedCharge')}
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												step="0.01"
												min={0}
												value={field.value ?? ''}
												onChange={e =>
													field.onChange(
														e.target.value
													)
												}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="lostTicketRateId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.lostTicketRate')}
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder={t('form.lostTicketRatePlaceholder')}
												autoComplete="off"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="reEntryChargeRateId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											{t('form.reEntryChargeRate')}
										</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder={t('form.reEntryChargeRatePlaceholder')}
												autoComplete="off"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</Card>
				</form>
			</Form>
		</>
	);
};
